/**
 * Knopf „Positionen aus Projektnotizen vorschlagen" im Maskenkopf (#158 F5, Spec Abschnitt 5).
 * Ruft die bestehende KI-Ableitung (`POST /v1/besetzung/projects/:id/extract`); die Vorschläge
 * erscheinen als Positionen mit Marke „KI-Vorschlag". Nur mit `staffing_manage` (Aufrufer prüft).
 *
 * Wie im alten Besetzungs-Panel erst eine Rückfrage — die Ableitung ersetzt eigene, freie
 * KI-Vorschläge. Es bleiben: bestätigte Positionen, Vorschläge mit Besetzung (Fix-Runde 1 (a),
 * Server `planExtractReplace`) und hereingeführte Positionen (`herkunft_project_id`). Die
 * Rückfrage nennt ersetzt / bleiben wegen Anhang / eigene bestätigte. Während des Laufs gesperrt.
 * Der Hinweis „Notiz geändert seit letzter Ableitung" fehlt bewusst: er bräuchte je Maske einen
 * zweiten Abruf (`GET /v1/besetzung/projects/:id`), im KL-Board also einen je Projekt.
 */
import type { StaffingMaske } from '@projektverwaltung/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../../lib/api.js';
import { useToast } from '../../toast.js';
import { useConfirm } from '../confirm-dialog.js';
import { maskeQueryKeys } from './maske-kontext.js';

function fehlerCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const p = e.payload as { error?: unknown } | null;
  if (p && typeof p.error === 'string') return p.error;
  return e.status === 503 ? 'ki_not_configured' : null;
}

/**
 * Zahlen für die Rückfrage — dieselbe Regel wie `planExtractReplace` auf dem Server: nur eigene
 * Positionen (ohne `herkunft_project_id`); ein Vorschlag „hängt", wenn eine Besetzung oder eine
 * ehemalige (ausgeplante) Besetzung an ihm hängt (#163: angehängt = hat Besetzung).
 */
export function ableitungsZahlen(maske: Pick<StaffingMaske, 'positionen' | 'ehemalige'>): {
  ersetzt: number;
  bleibt: number;
  bestaetigt: number;
} {
  const eigene = maske.positionen.filter((p) => !p.herkunft_project_id);
  const haengt = (id: string, p: (typeof eigene)[number]) =>
    p.besetzungen.length > 0 || maske.ehemalige.some((b) => b.beauftragte_position_id === id);
  const vorschlaege = eigene.filter((p) => p.status === 'ki_vorschlag');
  const bleibt = vorschlaege.filter((p) => haengt(p.beauftragte_position_id, p)).length;
  return {
    ersetzt: vorschlaege.length - bleibt,
    bleibt,
    bestaetigt: eigene.filter((p) => p.status === 'bestaetigt').length,
  };
}

export function KiAbleitungKnopf({
  projectId,
  maske,
}: {
  projectId: string;
  maske: Pick<StaffingMaske, 'positionen' | 'ehemalige'>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmAction = useConfirm();
  const qc = useQueryClient();
  const [laeuft, setLaeuft] = useState(false);

  const ableiten = async () => {
    setLaeuft(true);
    try {
      const res = await api.extractBeauftragtePositionen(projectId);
      await Promise.all(
        maskeQueryKeys(projectId).map((k) => qc.invalidateQueries({ queryKey: [...k] })),
      );
      if (res.created === 0) {
        toast.success(
          t(
            'v2.staffing_maske.ki.nichts_gefunden',
            'Keine Positionen in den Projektnotizen gefunden.',
          ),
        );
      } else {
        toast.success(
          t('v2.besetzung.extract_ok', '{{created}} Vorschläge erzeugt, {{replaced}} ersetzt', {
            created: res.created,
            replaced: res.replaced,
          }),
        );
      }
    } catch (e) {
      const code = fehlerCode(e);
      const titel = t('v2.besetzung.extract', 'Positionen aus Notizen vorschlagen');
      if (code === 'ki_not_configured') {
        toast.error(
          titel,
          t(
            'v2.staffing_maske.ki.nicht_konfiguriert',
            'Die KI ist nicht eingerichtet — bitte die Administration ansprechen.',
          ),
        );
      } else if (code === 'keine_projektnotiz') {
        toast.error(
          titel,
          t('v2.besetzung.extract_disabled', 'Der führende Auftrag hat keine Projektnotizen.'),
        );
      } else {
        toast.error(titel, e instanceof Error ? e.message : undefined);
      }
    } finally {
      setLaeuft(false);
    }
  };

  const starten = () => {
    const z = ableitungsZahlen(maske);
    void confirmAction({
      title: t('v2.besetzung.extract_confirm_title', 'Positionen neu vorschlagen?'),
      body:
        z.ersetzt + z.bleibt > 0
          ? t('v2.staffing_maske.ki.rueckfrage', { n: z.ersetzt, m: z.bleibt, k: z.bestaetigt })
          : t(
              'v2.besetzung.extract_confirm_none',
              'Es werden neue Vorschläge erzeugt. Fortfahren?',
            ),
      confirmLabel: t('v2.confirm.continue', 'Fortfahren'),
    }).then((ok) => {
      if (ok) void ableiten();
    });
  };

  return (
    <button
      type="button"
      className="text-xs hover:underline disabled:cursor-not-allowed disabled:opacity-60"
      style={{ color: 'var(--primary)' }}
      disabled={laeuft}
      onClick={starten}
    >
      {laeuft
        ? t('v2.staffing_maske.ki.laeuft', 'Positionen aus Projektnotizen vorschlagen — läuft…')
        : t('v2.staffing_maske.ki.knopf', 'Positionen aus Projektnotizen vorschlagen')}
    </button>
  );
}
